// src/components/BankManagement.jsx
import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { FaEye, FaTrash, FaEdit } from 'react-icons/fa';
import Swal from "sweetalert2";
import { Tooltip } from "@mui/material";
import BankFilter from "../../components/Table Filters/BankFilter";
import AddBankModal from './AddBankModal';
import EditBankModal from './EditBankModal';
import PermissionAccess from '../../PermissionAccess/Access.js';
import Alerts from "../../components/Alerts/alerts";
import AuthService from "../Authentication/auth.service";

const API_BASE_URL = process.env.REACT_APP_APIURL;

const BankManagement = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  // Helper function to refresh current page data
  const refreshCurrentPage = () => {
    fetchBanks(currentPage, pageLimit, filterText, filterStatus);
  };

  const [showModalAddBank, setShowModalAddBank] = useState(false);
  const handleToggleModalAddBank = () => {
    setShowModalAddBank(!showModalAddBank);
  }

  const [showModalEditBank, setShowModalEditBank] = useState(false);
  const handleToggleModalEditBank = (id) => {
    setShowModalEditBank(!showModalEditBank);
    setSelectedId(id);
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchBanks(1, pageLimit);
  }, [pageLimit]);

  function fetchBanks(page_index, page_limit, text, status) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    // Convert status string to boolean for backend
    let statusParam = '';
    if (status === 'Active') {
      statusParam = 'true';
    } else if (status === 'InActive') {
      statusParam = 'false';
    }

    console.log('Fetching banks with params:', { page_index, page_limit, text, status, statusParam });
    
    const requestData = {
      functionName: 'GetBanks',
      postData: {
        offset: offset,
        limit: page_limit,
        sort: { datetime_added: -1 },
        text: text || '',
        status: statusParam,
        noActiveMatch: true
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/GetBanks`, requestData, { headers }).then(
      (response) => {
        setLoading(false);
        console.log("Get banks response:-", response);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          console.log(response.data);
          const newTotalRows = response.data.count?.count || 0;
          const newBanks = response.data.data || [];
          
          // Check if current page is still valid after data changes
          const maxPage = Math.ceil(newTotalRows / page_limit) || 1;
          let adjustedPage = page_index;
          
          // If current page is beyond the available pages, go to the last available page
          if (page_index > maxPage && newTotalRows > 0) {
            adjustedPage = maxPage;
            setCurrentPage(maxPage);
            // Re-fetch with the adjusted page
            fetchBanks(maxPage, page_limit, text, status);
            return;
          }
          
          // If no data and we're not on page 1, go to page 1
          if (newTotalRows === 0 && page_index > 1) {
            adjustedPage = 1;
            setCurrentPage(1);
            // Re-fetch with page 1
            fetchBanks(1, page_limit, text, status);
            return;
          }
          
          setBanks(newBanks);
          setTotalRows(newTotalRows);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          // Check if it's an object with a message property
          if (typeof errContent === 'object' && errContent.message) {
            Alerts.swalErrorAlert(errContent.message);
          } else if (typeof errContent === 'string') {
            Alerts.swalErrorAlert(errContent);
          } else {
            Alerts.swalErrorAlert(t("Server Error, Please try again later."));
          }
      } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }

  function confirmDelete(_id) {
    let msg = t("Once deleted, you will not be able to recover this bank!")
    Alerts.swalConfirmDeleteAlert(msg).then(res => {
      console.log("Resp:-", res)
      if (res === true) {
        sendDelete(_id)
      }
    })
  };

  function sendDelete(id) {
    setLoading(true);
    const requestData = {
      functionName: 'DeleteBank',
      postData: {
        bank_id: id
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/DeleteBank`, requestData, { headers }).then((response) => {
      console.log("Delete Bank response:-", response)
      setLoading(false);
      refreshCurrentPage();
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Bank Deleted Successfully."))
        console.log(response.data.message)
      }
      else {
        setLoading(false);
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Bank."))
        }
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (typeof errContent === 'object' && errContent.message) {
          Alerts.swalErrorAlert(errContent.message);
        } else if (typeof errContent === 'string') {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Delete operation failed"));
        }
      })
  };

  const handleCheckboxChange = (e, row) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedRows((prevSelected) => [...prevSelected, row._id]);
    } else {
      setSelectedRows((prevSelected) => prevSelected.filter(id => id !== row._id));
    }
  };

  const handleSelectAllChange = (e) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedRows(banks.map((bank) => bank._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length > 0) {
      let msg = t(selectedRows.length + " records selected. Once deleted, you will not be able to recover this!");
      Alerts.swalConfirmDeleteAlert(msg).then(res => {
        console.log("Resp:-", res)
        if (res === true) {
          sendBulkDelete(selectedRows)
        }
      })
    } else {
      Alerts.swalErrorAlert('No records selected');
    }
  };

  function sendBulkDelete(selectedRows) {
    setLoading(true);
    const requestData = {
      functionName: 'DeleteBulkBanks',
      postData: {
        bank_ids: selectedRows
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/DeleteBulkBanks`, requestData, { headers }).then((response) => {
      console.log("Delete Bulk Banks response:-", response)
      setLoading(false);
      setSelectedRows([]);
      refreshCurrentPage();
      
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Banks Deleted Successfully."))
      } else {
        Alerts.swalErrorAlert(t("Some banks could not be deleted."))
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (typeof errContent === 'object' && errContent.message) {
          Alerts.swalErrorAlert(errContent.message);
        } else if (typeof errContent === 'string') {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Delete operation failed"));
        }
      })
  };

  const handleSearch = (searchText, status) => {
    setFilterText(searchText);
    setFilterStatus(status);
    if (searchText !== '' || status !== '') {
      fetchBanks(1, pageLimit, searchText, status)
    }
  };

  const handleClear = () => {
    setFilterText('');
    setFilterStatus('');
    fetchBanks(1, pageLimit);
  };

  function handlePageChange(page_index) {
    console.log("Page index:-", page_index)
    setCurrentPage(page_index)
    fetchBanks(page_index, pageLimit, filterText, filterStatus);
  }

  async function handlePerRowsChange(newPageLimit, page_index) {
    setPageLimit(newPageLimit);
  }

  const mycolumns = [
    {
      name: (
        <div>
            <input
            className="form-check-input"
            type="checkbox"
            onChange={handleSelectAllChange}
          />
          </div>
      ),
      width: '10%',
      selector: (row) => {
        return (
          <div>
            <input
              className="form-check-input"
              type="checkbox"
              checked={selectedRows.includes(row._id)}
              onChange={(e) => handleCheckboxChange(e, row)}
            />
          </div>
        );
      },
    },
    {
      name: t('Bank Name'),
      selector: "name",
      width: "30%",
      sortable: true,
    },
    {
      name: t('Status'),
      width: '30%',
      selector: (row) => {
        return (
          <div>
            {row.active === true || row.active === 'Active' ? (
              <button
                type="button"
                style={{ borderColor: '#3ac47d', backgroundColor: '#1BD35A' }}
                className="btn btn-sm btn-success btn-hover-shine me-3"
              >
                Active
              </button>
            ) : (
              <button
                type="button"
                style={{ borderColor: '#FF0000', backgroundColor: 'red' }}
                className="btn btn-sm btn-danger btn-hover-shine me-3"
              >
                InActive
              </button>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: t('Actions'),
      width: "30%",
      selector: (row) => {
        return (
          <div>
            {!super_admin ? current_module?.EditData === 1 &&
              <Tooltip placement="top" title={`${t("Update")} ${t("Bank")}`} arrow onClick={() => handleToggleModalEditBank(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 p-1"> </i>
              </Tooltip>
              :
              <Tooltip placement="top" title={`${t("Update")} ${t("Bank")}`} arrow onClick={() => handleToggleModalEditBank(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 p-1"> </i>
              </Tooltip>
            }
            {!super_admin ? current_module?.DeleteData === 1 &&
              <Tooltip placement="top" title={`${t("Delete")} ${t("Bank")}`} arrow onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-trash text-danger btn-icon-wrapper fs-5"> </i>
              </Tooltip>
              :
              <Tooltip placement="top" title={`${t("Delete")} ${t("Bank")}`} arrow onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-trash text-danger btn-icon-wrapper fs-5"> </i>
              </Tooltip>
            }
          </div>
        );
      }
    },
  ];

  const filteredItems = banks;

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <BankFilter
        onSearch={handleSearch}
        onClear={handleClear}
        filterText={filterText}
        filterStatus={filterStatus}
      />
    );
  }, [filterText, filterStatus]);

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-credit icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {`${t("Banks")} ${t("Management")}`}
                        <div className="page-title-subheading">
                          {`${t("Add, Update & Delete of")} ${t("Banks")}`}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <AddBankModal showModalAddBank={showModalAddBank} handleToggleModalAddBank={handleToggleModalAddBank} onSuccess={refreshCurrentPage} />
                      <EditBankModal
                        onSuccess={refreshCurrentPage}
                        showModalEditBank={showModalEditBank}
                        handleToggleModalEditBank={() => handleToggleModalEditBank(null)}
                        id={selectedId}
                      />
                    </div>
                  </div>
                </div>
                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <Col md="6" style={{ float: "left", position: "absolute", zIndex: "1" }}>
                            {subHeaderComponentMemo}
                          </Col>
                          <DataTable
                            data={filteredItems}
                            columns={mycolumns}
                            pagination
                            fixedHeader
                            fixedHeaderScrollHeight="400px"
                            subHeader
                            subHeaderComponent={
                              <div>
                                {!super_admin ? current_module?.BulkDelete === 1 &&
                                  <button type="button"
                                    className="btn btn-outline-danger btn-hover-shine me-3 btn"
                                    onClick={handleDeleteSelected}
                                  >
                                    {t('Delete Selected')}
              </button>
                                  :
                                  <button type="button"
                                    className="btn btn-outline-danger btn-hover-shine me-3 btn"
                                    onClick={handleDeleteSelected}
                                  >
                                    {t('Delete Selected')}
                                  </button>
                                }
                                
                                {!super_admin ? current_module?.AddData === 1 &&
                                  <button type="button" className="btn btn-custom-color btn-hover-shine me-3 btn" onClick={handleToggleModalAddBank}>
                                    {t('Add New')}
                                  </button>
                                  :
                                  <button type="button" className="btn btn-custom-color btn-hover-shine me-3 btn" onClick={handleToggleModalAddBank}>
                                    {t('Add New')}
              </button>
                                }
                              </div>
                            }
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
    </div>
    </Fragment>
  );
};

export default BankManagement;